import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from '@angular/core';

import { fetchMemberActiveCoverage, selectMemberActiveCoverage } from '@lib/members/data-access';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';

import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';

import { PackageCoverageDialogComponent, PackageCoverageDialogData } from '../package-coverage-dialog/package-coverage-dialog.component';

@Component({
  selector: 'members-package-coverage-button',
  standalone: true,
  imports: [MatIcon, MatProgressSpinner, MatTooltip, TranslatePipe],
  templateUrl: './package-coverage-button.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PackageCoverageButtonComponent {
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);

  readonly userId = input.required<string>();

  private readonly activeCoverage = this.store.selectSignal(selectMemberActiveCoverage);
  private readonly coverage = computed(() => {
    const coverage = this.activeCoverage().coverage;
    return coverage?.userId === this.userId() ? coverage : null;
  });

  protected readonly opening = signal<boolean>(false);

  constructor() {
    effect(() => {
      const coverage = this.coverage();
      if (!this.opening() || !coverage || coverage.loading) return;

      untracked(() => {
        this.opening.set(false);
        this.openDialog();
      });
    });
  }

  protected openCoverage(): void {
    if (this.opening()) return;

    const coverage = this.coverage();
    if (coverage && !coverage.loading && !coverage.error) return this.openDialog();

    if (!coverage?.loading) this.store.dispatch(fetchMemberActiveCoverage({ userId: this.userId() }));
    this.opening.set(true);
  }

  private openDialog(): void {
    const data: PackageCoverageDialogData = { userId: this.userId() };
    this.dialog.open(PackageCoverageDialogComponent, { data, width: '1100px', maxWidth: '95vw', maxHeight: '90vh', autoFocus: false });
  }
}
